import axios from 'axios';
import { GitHubData, GitHubResource, ApiResponse, InfrastructureError } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class GitHubService {
  private static instance: GitHubService;
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): GitHubService {
    if (!GitHubService.instance) {
      GitHubService.instance = new GitHubService();
    }
    return GitHubService.instance;
  }

  private async apiRequest<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        console.warn('GitHub Service: No auth token - request may fail');
        // Let backend handle auth validation
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/infrastructure/github${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // Reduced to 10 seconds
      });
      return response.data;
    } catch (error: any) {
      console.error(`GitHub API Error for ${endpoint}:`, error);
      throw {
        code: error.response?.status || 'NETWORK_ERROR',
        message: error.response?.data?.message || error.message || 'Failed to fetch GitHub data',
        service: 'github',
        timestamp: new Date().toISOString()
      } as InfrastructureError;
    }
  }

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data as T;
    }
    return null;
  }

  private setCachedData<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async getRepositories(organization?: string): Promise<GitHubResource[]> {
    const cacheKey = `repos-${organization || 'default'}`;
    const cached = this.getCachedData<GitHubResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);

    const response = await this.apiRequest<GitHubResource[]>(`/repositories?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch repositories');
  }

  async getWorkflows(organization?: string, repository?: string): Promise<any> {
    const cacheKey = `workflows-${organization || 'default'}-${repository || 'all'}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);
    if (repository) params.append('repository', repository);

    const response = await this.apiRequest<any>(`/workflows?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch workflows');
  }

  async getWorkflowRuns(organization?: string, repository?: string): Promise<any> {
    const cacheKey = `workflow-runs-${organization || 'default'}-${repository || 'all'}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);
    if (repository) params.append('repository', repository);

    const response = await this.apiRequest<any>(`/workflows/runs?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch workflow runs');
  }

  async getIssues(organization?: string, repository?: string): Promise<GitHubResource[]> {
    const cacheKey = `issues-${organization || 'default'}-${repository || 'all'}`;
    const cached = this.getCachedData<GitHubResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);
    if (repository) params.append('repository', repository);

    const response = await this.apiRequest<GitHubResource[]>(`/issues?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch issues');
  }

  async getPullRequests(organization?: string, repository?: string): Promise<GitHubResource[]> {
    const cacheKey = `pulls-${organization || 'default'}-${repository || 'all'}`;
    const cached = this.getCachedData<GitHubResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);
    if (repository) params.append('repository', repository);

    const response = await this.apiRequest<GitHubResource[]>(`/pulls?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch pull requests');
  }

  async getCommits(organization?: string, repository?: string, since?: string): Promise<any> {
    const cacheKey = `commits-${organization || 'default'}-${repository || 'all'}-${since || '7d'}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);
    if (repository) params.append('repository', repository);
    if (since) params.append('since', since);

    const response = await this.apiRequest<any>(`/commits?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch commits');
  }

  async getRepositoryStats(organization?: string): Promise<any> {
    const cacheKey = `repo-stats-${organization || 'default'}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (organization) params.append('organization', organization);

    const response = await this.apiRequest<any>(`/stats/repositories?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch repository stats');
  }

  async getAllData(organization?: string): Promise<GitHubData> {
    try {
      const [
        repositories,
        workflowData,
        workflowRuns,
        issues,
        pullRequests,
        commits,
        stats
      ] = await Promise.allSettled([
        this.getRepositories(organization),
        this.getWorkflows(organization),
        this.getWorkflowRuns(organization),
        this.getIssues(organization),
        this.getPullRequests(organization),
        this.getCommits(organization),
        this.getRepositoryStats(organization)
      ]);

      const repoData = repositories.status === 'fulfilled' ? repositories.value : [];
      const workflows = workflowData.status === 'fulfilled' ? workflowData.value : { total: 0, active: 0 };
      const runs = workflowRuns.status === 'fulfilled' ? workflowRuns.value : { successful: 0, failed: 0 };
      const issueData = issues.status === 'fulfilled' ? issues.value : [];
      const prData = pullRequests.status === 'fulfilled' ? pullRequests.value : [];
      const commitData = commits.status === 'fulfilled' ? commits.value : { count: 0 };
      const repoStats = stats.status === 'fulfilled' ? stats.value : {};

      return {
        repositories: {
          list: repoData,
          total: repoData.length,
          public: repoData.filter(r => r.visibility === 'public').length,
          private: repoData.filter(r => r.visibility === 'private').length
        },
        workflows: {
          total: workflows.total || 0,
          active: workflows.active || 0,
          successful: runs.successful || 0,
          failed: runs.failed || 0
        },
        stats: {
          commits: commitData.count || 0,
          pullRequests: prData.length,
          issues: issueData.length
        }
      };
    } catch (error) {
      console.error('Error fetching GitHub data:', error);
      throw error;
    }
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const githubService = GitHubService.getInstance();